import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Header,
  Segment,
  TextArea,
  Message,
} from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import {
  addHistoryToDb,
  addNotificationToDb,
  sendPartyFormReviewedNotificationEmail,
  buildNotificationRecipientIds,
} from "../../../app/firestore/firestoreService";

export default function PartyNotifyAgentModal({ doc, transaction, party }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sanitize input to prevent weird characters
  function sanitizeInput(input) {
    // Remove control characters and other potentially problematic characters
    // Keep letters, numbers, spaces, and common punctuation
    // eslint-disable-next-line no-useless-escape
    return input.replace(/[^\w\s.,!?;:()\-'"@#$%&*+=\[\]{}/<>]/g, "");
  }

  function handleMessageChange(e) {
    const sanitized = sanitizeInput(e.target.value);
    setMessage(sanitized);
  }

  async function handleNotifyAgent() {
    if (!isMountedRef.current) return;
    setIsSubmitting(true);
    try {
      // Build recipient IDs list (all team members who should see this notification)
      const recipientIds = await buildNotificationRecipientIds(transaction);

      if (!isMountedRef.current) return;

      // Add notification to database
      await addNotificationToDb({
        transactionId: transaction.id,
        docId: doc.id,
        docName: doc.name,
        partyId: party.id,
        partyRole: party.role,
        partyFirstName: party.firstName,
        partyLastName: party.lastName,
        message: message.trim(),
        type: "formReviewed",
        status: "unresolved",
        transactionAddress: transaction.address?.street || transaction.title || "",
        agentId: transaction.userId,
        recipientIds: recipientIds,
      });

      if (!isMountedRef.current) return;

      // Add to history collection
      await addHistoryToDb(
        transaction.id,
        party,
        "notified agent - done editing/reviewing",
        doc.name
      );

      if (!isMountedRef.current) return;

      // Send email to agent and their team
      await sendPartyFormReviewedNotificationEmail(
        transaction,
        doc,
        party,
        message.trim()
      );

      if (!isMountedRef.current) return;

      toast.success("Agent has been notified!");
      dispatch(
        closeModal({
          modalType: "PartyNotifyAgentModal",
        })
      );
    } catch (error) {
      console.error("Error notifying agent:", error);
      if (isMountedRef.current) {
        toast.error("Failed to notify agent. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  function handleCancel() {
    dispatch(
      closeModal({
        modalType: "PartyNotifyAgentModal",
      })
    );
  }

  return (
    <ModalWrapper size="small">
      <Segment>
        <Grid>
          <Grid.Column>
            <Header size="large" color="blue">
              Notify Agent
            </Header>
            <Divider />
            <p>
              Let your agent know that you have finished reviewing or filling
              out this document, or send a quick message or question to your agent.
            </p>
            <p>
              <strong>Document:</strong> {doc.name}
            </p>
            <Divider />
            <label>
              <strong>Message (Optional):</strong>
            </label>
            <TextArea
              rows={4}
              value={message}
              onChange={handleMessageChange}
              placeholder="Add any notes or questions for your agent..."
              style={{ width: "100%", marginTop: "10px" }}
              disabled={isSubmitting}
            />
            <Message info size="small" style={{ marginTop: "10px" }}>
              Your agent and their team will be notified via email.
            </Message>
            <Divider />
            <Button
              disabled={isSubmitting}
              onClick={handleCancel}
              type="button"
              floated={isMobile ? null : "right"}
              content="Cancel"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
            <Button
              primary
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={handleNotifyAgent}
              type="button"
              floated={isMobile ? null : "right"}
              content="Notify Agent I'm Done"
              className={isMobile ? "fluid medium bottom margin" : null}
            />
          </Grid.Column>
        </Grid>
      </Segment>
    </ModalWrapper>
  );
}

